// ─── Single source of truth for the AI model catalog ────────────────────────
//
// This module owns the provider/model catalog, per-model pricing, and the
// UI-id → { provider, model } routing logic. It is consumed by:
//   - the shared chat-drawer component (chat-drawer.tsx)
//   - chat.askfinz.ai's catalog API route (app/api/models/route.ts)
//   - the askFinz browser extension (fetches the API route cross-origin)
//
// Keeping it here means the catalog auto-grows for every surface when a model
// is added — nothing is hardcoded per-surface anymore.

export type ModelCategory = "all" | "fast" | "reasoning" | "coding" | "vision" | "free"

export interface ProviderModel {
  id: string
  provider: string
  providerLabel: string
  providerInitials: string
  providerColor: string   // tailwind bg + text classes for badge
  logoPath?: string       // /shared/logos/providers/{name}.svg when available
  name: string
  tags: string[]
  description: string
  useCases: string[]
  rpm?: number            // requests per minute limit
  rpd?: number            // requests per day limit
  tpm?: number            // tokens per minute
  resetLabel: string      // human-readable reset period
  contextK: number        // context window in K tokens
  categories: ModelCategory[]
}

export const PROVIDER_MODELS: ProviderModel[] = [
  // ── askFinz (Anthropic) ──────────────────────────────────────────────────
  {
    id: "askfinz:balanced", provider: "askfinz", providerLabel: "askFinz AI",
    providerInitials: "AF", providerColor: "bg-accent/20 text-accent",
    name: "askFinz Balanced", tags: ["balanced", "reasoning", "vision"],
    description: "Best balance of intelligence and speed. Excels at complex reasoning, code generation, and nuanced analysis tasks.",
    useCases: ["Code review", "Data analysis", "Long-form writing", "Complex Q&A"],
    resetLabel: "pay-per-use", contextK: 200,
    categories: ["all", "reasoning", "coding", "vision"],
  },
  {
    id: "askfinz:pro", provider: "askfinz", providerLabel: "askFinz AI",
    providerInitials: "AF", providerColor: "bg-accent/20 text-accent",
    name: "askFinz Pro", tags: ["most powerful", "deep reasoning", "vision"],
    description: "Most capable model for the hardest problems. Deep reasoning, research-grade analysis, and complex multi-step tasks.",
    useCases: ["Scientific research", "Agentic workflows", "Architecture decisions", "Strategy"],
    resetLabel: "pay-per-use", contextK: 200,
    categories: ["all", "reasoning", "coding", "vision"],
  },
  {
    id: "askfinz:fast", provider: "askfinz", providerLabel: "askFinz AI",
    providerInitials: "AF", providerColor: "bg-accent/20 text-accent",
    name: "askFinz Fast", tags: ["fastest", "low-cost", "vision"],
    description: "Ultra-fast and cost-efficient. Perfect for high-frequency tasks, quick answers, and real-time applications.",
    useCases: ["Classification", "Summarization", "Quick lookups", "High-volume pipelines"],
    resetLabel: "pay-per-use", contextK: 200,
    categories: ["all", "fast", "vision"],
  },
  // ── Google AI Studio ─────────────────────────────────────────────────────
  {
    id: "gemini-2.5-flash", provider: "google", providerLabel: "Google AI Studio",
    providerInitials: "GG", providerColor: "bg-blue-500/20 text-blue-400",
    logoPath: "/shared/logos/providers/google.svg",
    name: "Gemini 2.5 Flash", tags: ["multimodal", "vision", "thinking"],
    description: "Google's fast multimodal model with excellent vision understanding and optional extended thinking capability.",
    useCases: ["Image analysis", "Document Q&A", "Fast inference", "Research assistance"],
    rpm: 5, rpd: 20, tpm: 250000, resetLabel: "daily",
    contextK: 1000, categories: ["all", "fast", "vision"],
  },
  {
    id: "gemini-3-flash-preview", provider: "google", providerLabel: "Google AI Studio",
    providerInitials: "GG", providerColor: "bg-blue-500/20 text-blue-400",
    logoPath: "/shared/logos/providers/google.svg",
    name: "Gemini 3 Flash", tags: ["latest", "multimodal", "vision"],
    description: "Google's newest Flash — faster, smarter, more efficient with a massive 1M token context window.",
    useCases: ["Real-time chat", "Code generation", "Document analysis", "Content creation"],
    rpm: 5, rpd: 20, tpm: 250000, resetLabel: "daily",
    contextK: 1000, categories: ["all", "fast", "vision"],
  },
  {
    id: "gemini-flash-latest", provider: "google", providerLabel: "Google AI Studio",
    providerInitials: "GG", providerColor: "bg-blue-500/20 text-blue-400",
    logoPath: "/shared/logos/providers/google.svg",
    name: "Gemini Flash (latest)", tags: ["alias", "stable", "auto-update"],
    description: "Always-current pointer to Google's latest stable Flash model. Free tier; quotas reset daily.",
    useCases: ["Default fallback", "General chat", "Drafting", "Research"],
    rpm: 10, rpd: 250, tpm: 250000, resetLabel: "daily",
    contextK: 1000, categories: ["all", "fast", "vision"],
  },
  {
    id: "gemini-2.5-flash-lite", provider: "google", providerLabel: "Google AI Studio",
    providerInitials: "GG", providerColor: "bg-blue-500/20 text-blue-400",
    logoPath: "/shared/logos/providers/google.svg",
    name: "Gemini 2.5 Flash-Lite", tags: ["ultra-fast", "efficient"],
    description: "Lightest Gemini model — optimized for latency-critical and edge applications.",
    useCases: ["Classification", "Quick lookups", "Real-time apps", "Edge deployment"],
    rpm: 10, rpd: 20, tpm: 250000, resetLabel: "daily",
    contextK: 1000, categories: ["all", "fast"],
  },
  {
    id: "gemma-3-27b-it", provider: "google", providerLabel: "Google AI Studio",
    providerInitials: "GG", providerColor: "bg-blue-500/20 text-blue-400",
    logoPath: "/shared/logos/providers/google.svg",
    name: "Gemma 3 27B", tags: ["open model", "text", "coding"],
    description: "Google's open-weight 27B model. Strong reasoning and coding, runnable on-device or via API.",
    useCases: ["Code generation", "Reasoning tasks", "Local/private deployment"],
    rpm: 30, rpd: 14400, tpm: 15000, resetLabel: "daily",
    contextK: 128, categories: ["all", "coding"],
  },
  {
    id: "gemma-3-12b-it", provider: "google", providerLabel: "Google AI Studio",
    providerInitials: "GG", providerColor: "bg-blue-500/20 text-blue-400",
    logoPath: "/shared/logos/providers/google.svg",
    name: "Gemma 3 12B", tags: ["open model", "fast", "efficient"],
    description: "Compact open-weight model with strong benchmark performance for its size.",
    useCases: ["Lightweight tasks", "Edge use", "Fine-tuning base"],
    rpm: 30, rpd: 14400, tpm: 15000, resetLabel: "daily",
    contextK: 128, categories: ["all", "fast", "coding"],
  },
  // ── NVIDIA NIM ───────────────────────────────────────────────────────────
  {
    id: "nvidia/deepseek-v4-pro", provider: "nvidia", providerLabel: "NVIDIA NIM",
    providerInitials: "NV", providerColor: "bg-green-500/20 text-green-400",
    logoPath: "/shared/logos/providers/nvidia.svg",
    name: "DeepSeek V4 Pro", tags: ["coding", "agentic", "long-context", "MoE"],
    description: "685B MoE model with 1M-token context. State-of-the-art coding and integrated agentic tool use.",
    useCases: ["Complex coding", "Agentic pipelines", "Long document analysis", "Research"],
    rpm: 40, resetLabel: "minute",
    contextK: 1000, categories: ["all", "coding", "reasoning"],
  },
  {
    id: "nvidia/deepseek-v4-flash", provider: "nvidia", providerLabel: "NVIDIA NIM",
    providerInitials: "NV", providerColor: "bg-green-500/20 text-green-400",
    logoPath: "/shared/logos/providers/nvidia.svg",
    name: "DeepSeek V4 Flash", tags: ["coding", "fast", "MoE"],
    description: "Fast 284B MoE variant optimized for coding and agent tasks with 1M context window.",
    useCases: ["Fast code generation", "Code completion", "Agent tool calls"],
    rpm: 40, resetLabel: "minute",
    contextK: 1000, categories: ["all", "fast", "coding"],
  },
  {
    id: "nvidia/llama-4-maverick", provider: "nvidia", providerLabel: "NVIDIA NIM",
    providerInitials: "NV", providerColor: "bg-green-500/20 text-green-400",
    logoPath: "/shared/logos/providers/nvidia.svg",
    name: "Llama 4 Maverick", tags: ["multimodal", "multilingual", "MoE"],
    description: "Meta's latest 128-expert MoE multimodal model. Strong multilingual and vision understanding.",
    useCases: ["Multilingual chat", "Image understanding", "General-purpose tasks"],
    rpm: 40, resetLabel: "minute",
    contextK: 128, categories: ["all", "vision"],
  },
  {
    id: "nvidia/kimi-k2-instruct", provider: "nvidia", providerLabel: "NVIDIA NIM",
    providerInitials: "NV", providerColor: "bg-green-500/20 text-green-400",
    name: "Kimi K2", tags: ["reasoning", "coding", "long-context"],
    description: "Moonshot AI's open MoE model with 256K context, native INT4 quantization, and enhanced tool use.",
    useCases: ["Reasoning chains", "Code with tools", "Research", "Long-form analysis"],
    rpm: 40, resetLabel: "minute",
    contextK: 256, categories: ["all", "reasoning", "coding"],
  },
  {
    id: "nvidia/minimax-m2.7", provider: "nvidia", providerLabel: "NVIDIA NIM",
    providerInitials: "NV", providerColor: "bg-green-500/20 text-green-400",
    name: "MiniMax M2.7", tags: ["coding", "reasoning", "office"],
    description: "230B-parameter model excelling in coding, reasoning, and document/office productivity tasks.",
    useCases: ["Document processing", "Code generation", "Office automation"],
    rpm: 40, resetLabel: "minute",
    contextK: 128, categories: ["all", "coding", "reasoning"],
  },
  {
    id: "nvidia/qwen3-coder-480b", provider: "nvidia", providerLabel: "NVIDIA NIM",
    providerInitials: "NV", providerColor: "bg-green-500/20 text-green-400",
    name: "Qwen 3 Coder 480B", tags: ["agentic coding", "browser use", "256K"],
    description: "Top agentic coding model with browser use support and 256K context. Leading coding benchmarks.",
    useCases: ["Agentic coding", "Browser automation", "Complex code tasks"],
    rpm: 40, resetLabel: "minute",
    contextK: 256, categories: ["all", "coding"],
  },
  // ── Mistral ──────────────────────────────────────────────────────────────
  {
    id: "mistral-large-3", provider: "mistral", providerLabel: "Mistral AI",
    providerInitials: "MI", providerColor: "bg-orange-500/20 text-orange-400",
    logoPath: "/shared/logos/providers/mistral.svg",
    name: "Mistral Large 3", tags: ["agentic", "coding", "multilingual", "vision"],
    description: "State-of-the-art MoE VLM for agentic tasks, function calling, and instruction following in 30+ languages.",
    useCases: ["Agentic workflows", "Code generation", "Enterprise tasks", "Multilingual"],
    rpm: 1, tpm: 500000, resetLabel: "monthly",
    contextK: 128, categories: ["all", "coding", "reasoning"],
  },
  {
    id: "mistral-medium-3", provider: "mistral", providerLabel: "Mistral AI",
    providerInitials: "MI", providerColor: "bg-orange-500/20 text-orange-400",
    logoPath: "/shared/logos/providers/mistral.svg",
    name: "Mistral Medium 3", tags: ["multimodal", "balanced", "enterprise"],
    description: "Powerful multimodal model for enterprise: software development, data analysis, and complex reasoning.",
    useCases: ["Data analysis", "Document Q&A", "Code review", "Vision tasks"],
    rpm: 1, tpm: 500000, resetLabel: "monthly",
    contextK: 128, categories: ["all", "vision", "coding"],
  },
  {
    id: "magistral-small", provider: "mistral", providerLabel: "Mistral AI",
    providerInitials: "MI", providerColor: "bg-orange-500/20 text-orange-400",
    name: "Magistral Small", tags: ["reasoning", "efficient", "coding", "256K"],
    description: "High-performance reasoning model optimized for efficiency and edge deployment with 256K context.",
    useCases: ["Reasoning tasks", "Code debugging", "Edge deployment", "Cost-efficient"],
    rpm: 1, tpm: 500000, resetLabel: "monthly",
    contextK: 256, categories: ["all", "reasoning", "fast"],
  },
  // ── Cerebras ─────────────────────────────────────────────────────────────
  {
    id: "cerebras/qwen-3-235b", provider: "cerebras", providerLabel: "Cerebras",
    providerInitials: "CB", providerColor: "bg-red-500/20 text-red-400",
    logoPath: "/shared/logos/providers/cerebras.svg",
    name: "Qwen 3 235B (Cerebras)", tags: ["ultra-fast", "wafer-scale", "large"],
    description: "235B MoE model at Cerebras wafer-scale speed — massive capability with ultra-low latency.",
    useCases: ["Complex reasoning", "High-volume inference", "Speed-critical large-model tasks"],
    rpm: 30, rpd: 14400, tpm: 60000, resetLabel: "daily",
    contextK: 128, categories: ["all", "fast", "reasoning"],
  },
  {
    id: "cerebras/llama-3.1-8b", provider: "cerebras", providerLabel: "Cerebras",
    providerInitials: "CB", providerColor: "bg-red-500/20 text-red-400",
    logoPath: "/shared/logos/providers/cerebras.svg",
    name: "Llama 3.1 8B (Cerebras)", tags: ["ultra-fast", "lightweight"],
    description: "Llama 3.1 8B on Cerebras silicon — extreme inference speed for lightweight real-time tasks.",
    useCases: ["Low-latency apps", "Classification", "Real-time Q&A", "Streaming"],
    rpm: 30, rpd: 14400, tpm: 60000, resetLabel: "daily",
    contextK: 128, categories: ["all", "fast"],
  },
  // ── Groq ─────────────────────────────────────────────────────────────────
  {
    id: "groq/llama-4-scout", provider: "groq", providerLabel: "Groq",
    providerInitials: "GQ", providerColor: "bg-yellow-500/20 text-yellow-400",
    logoPath: "/shared/logos/providers/groq.svg",
    name: "Llama 4 Scout (Groq)", tags: ["fast", "multimodal", "30K TPM"],
    description: "Meta Llama 4 Scout on GroqChip — 30K tokens/min throughput with multimodal support.",
    useCases: ["Fast multimodal tasks", "Code generation", "High-throughput chat"],
    rpm: 30, rpd: 1000, tpm: 30000, resetLabel: "daily",
    contextK: 128, categories: ["all", "fast", "vision"],
  },
  {
    id: "groq/llama-3.3-70b", provider: "groq", providerLabel: "Groq",
    providerInitials: "GQ", providerColor: "bg-yellow-500/20 text-yellow-400",
    logoPath: "/shared/logos/providers/groq.svg",
    name: "Llama 3.3 70B (Groq)", tags: ["fast", "capable", "12K TPM"],
    description: "Strong 70B model at GroqChip speed — production-grade throughput for complex tasks.",
    useCases: ["Production inference", "Complex reasoning", "Document analysis"],
    rpm: 30, rpd: 1000, tpm: 12000, resetLabel: "daily",
    contextK: 128, categories: ["all", "fast", "reasoning"],
  },
  {
    id: "groq/qwen3-32b", provider: "groq", providerLabel: "Groq",
    providerInitials: "GQ", providerColor: "bg-yellow-500/20 text-yellow-400",
    name: "Qwen 3 32B (Groq)", tags: ["reasoning", "multilingual", "fast"],
    description: "Alibaba Qwen 3 32B on GroqChip. Strong multilingual reasoning and instruction following.",
    useCases: ["Multilingual tasks", "Reasoning", "Coding assistance"],
    rpm: 30, rpd: 1000, tpm: 6000, resetLabel: "daily",
    contextK: 32, categories: ["all", "reasoning"],
  },
  {
    id: "groq/compound", provider: "groq", providerLabel: "Groq",
    providerInitials: "GQ", providerColor: "bg-yellow-500/20 text-yellow-400",
    name: "Groq Compound", tags: ["compound AI", "search-augmented"],
    description: "Groq's compound AI system with real-time web search and information retrieval built in.",
    useCases: ["Real-time research", "Fact-checking", "Current events", "Search-grounded answers"],
    rpm: 30, rpd: 250, tpm: 70000, resetLabel: "daily",
    contextK: 128, categories: ["all", "reasoning"],
  },
  // ── Cohere ───────────────────────────────────────────────────────────────
  {
    id: "cohere/command-a", provider: "cohere", providerLabel: "Cohere",
    providerInitials: "CO", providerColor: "bg-purple-500/20 text-purple-400",
    logoPath: "/shared/logos/providers/cohere.svg",
    name: "Command A", tags: ["agentic", "tool use", "enterprise", "256K"],
    description: "Cohere's most powerful model for agentic and enterprise workflows with advanced tool use capabilities.",
    useCases: ["Agentic pipelines", "Enterprise RAG", "Tool calling", "Complex workflows"],
    rpm: 20, tpm: 500000, resetLabel: "monthly",
    contextK: 256, categories: ["all", "reasoning", "coding"],
  },
  {
    id: "cohere/command-r-plus", provider: "cohere", providerLabel: "Cohere",
    providerInitials: "CO", providerColor: "bg-purple-500/20 text-purple-400",
    logoPath: "/shared/logos/providers/cohere.svg",
    name: "Command R+", tags: ["RAG", "multilingual", "citation"],
    description: "Optimized for retrieval-augmented generation with high citation accuracy across 10 languages.",
    useCases: ["Enterprise search", "Document Q&A", "Multi-lingual support", "Citation-grounded answers"],
    rpm: 20, tpm: 500000, resetLabel: "monthly",
    contextK: 128, categories: ["all", "reasoning"],
  },
  // ── GitHub Models ─────────────────────────────────────────────────────────
  {
    id: "github/gpt-5", provider: "github", providerLabel: "GitHub Models",
    providerInitials: "GH", providerColor: "bg-gray-500/20 text-gray-300",
    logoPath: "/shared/logos/providers/github.svg",
    name: "OpenAI GPT-5", tags: ["frontier", "reasoning", "vision", "Copilot Pro+"],
    description: "OpenAI's flagship frontier model via GitHub Models. Requires Copilot Pro+ subscription.",
    useCases: ["Complex reasoning", "Research", "Advanced coding", "Creative tasks"],
    rpm: 1, rpd: 8, tpm: 4000, resetLabel: "daily",
    contextK: 128, categories: ["all", "reasoning", "vision", "coding"],
  },
  {
    id: "github/gpt-4o", provider: "github", providerLabel: "GitHub Models",
    providerInitials: "GH", providerColor: "bg-gray-500/20 text-gray-300",
    logoPath: "/shared/logos/providers/github.svg",
    name: "OpenAI GPT-4o", tags: ["multimodal", "balanced", "vision"],
    description: "OpenAI's leading multimodal model via GitHub Models. Fast, capable, vision-enabled.",
    useCases: ["Code review", "Vision tasks", "General chat", "Summarization"],
    rpm: 10, rpd: 50, tpm: 8000, resetLabel: "daily",
    contextK: 128, categories: ["all", "vision", "coding"],
  },
  {
    id: "github/deepseek-r1", provider: "github", providerLabel: "GitHub Models",
    providerInitials: "GH", providerColor: "bg-gray-500/20 text-gray-300",
    logoPath: "/shared/logos/providers/github.svg",
    name: "DeepSeek R1", tags: ["reasoning", "open-source", "math"],
    description: "DeepSeek's open reasoning model — strong math, logic, coding, and step-by-step problem solving.",
    useCases: ["Mathematical reasoning", "Proof-checking", "Step-by-step analysis", "Open-source workflows"],
    rpm: 1, rpd: 8, tpm: 4000, resetLabel: "daily",
    contextK: 64, categories: ["all", "reasoning"],
  },
  {
    id: "github/grok-3", provider: "github", providerLabel: "GitHub Models",
    providerInitials: "GH", providerColor: "bg-gray-500/20 text-gray-300",
    logoPath: "/shared/logos/providers/github.svg",
    name: "xAI Grok 3", tags: ["conversational", "balanced", "real-time"],
    description: "xAI's Grok 3 via GitHub Models. Conversational, balanced across reasoning and creative coding.",
    useCases: ["Conversational AI", "Code assistance", "Creative writing", "Real-time queries"],
    rpm: 1, rpd: 15, tpm: 4000, resetLabel: "daily",
    contextK: 128, categories: ["all", "reasoning", "coding"],
  },
  {
    id: "github/phi-4", provider: "github", providerLabel: "GitHub Models",
    providerInitials: "GH", providerColor: "bg-gray-500/20 text-gray-300",
    name: "Phi-4", tags: ["small", "reasoning", "efficient", "STEM"],
    description: "Microsoft's Phi-4 — small but surprisingly capable for STEM reasoning and instruction following.",
    useCases: ["On-device AI", "Cost-efficient reasoning", "STEM tasks", "Education"],
    rpm: 10, rpd: 150, tpm: 8000, resetLabel: "daily",
    contextK: 16, categories: ["all", "reasoning", "fast"],
  },
  // ── AWS Bedrock Enterprise ─────────────────────────────────────────────────
  {
    id: "askfinz:bedrock-standard", provider: "bedrock", providerLabel: "Enterprise Cloud (Bedrock)",
    providerInitials: "EC", providerColor: "bg-amber-500/20 text-amber-400",
    name: "askFinz Standard (Enterprise)", tags: ["enterprise", "VPC", "secure", "vision"],
    description: "askFinz Balanced on AWS Bedrock — data stays in your VPC with enterprise compliance guarantees.",
    useCases: ["Enterprise workloads", "Compliance-critical tasks", "Private data processing"],
    resetLabel: "pay-per-use", contextK: 200,
    categories: ["all", "reasoning", "coding", "vision"],
  },
  {
    id: "askfinz:bedrock-fast", provider: "bedrock", providerLabel: "Enterprise Cloud (Bedrock)",
    providerInitials: "EC", providerColor: "bg-amber-500/20 text-amber-400",
    name: "askFinz Fast (Enterprise)", tags: ["enterprise", "fast", "VPC"],
    description: "askFinz Fast on AWS Bedrock — ultra-low latency with enterprise data residency.",
    useCases: ["High-volume enterprise", "Real-time enterprise apps", "Low-latency production"],
    resetLabel: "pay-per-use", contextK: 200,
    categories: ["all", "fast"],
  },
  {
    id: "askfinz:bedrock-pro", provider: "bedrock", providerLabel: "Enterprise Cloud (Bedrock)",
    providerInitials: "EC", providerColor: "bg-amber-500/20 text-amber-400",
    name: "askFinz Pro (Enterprise)", tags: ["enterprise", "most powerful", "VPC"],
    description: "askFinz Pro on AWS Bedrock — maximum capability with enterprise data sovereignty.",
    useCases: ["Strategic enterprise decisions", "Complex research", "Sensitive data"],
    resetLabel: "pay-per-use", contextK: 200,
    categories: ["all", "reasoning"],
  },
]

// ─── Token cost pricing ───────────────────────────────────────────────────────

export type ModelPricing = { inputPerMTok: number; outputPerMTok: number; cachedPerMTok?: number; freeTier?: boolean; free?: boolean }

export const MODEL_PRICING: Record<string, ModelPricing> = {
  "askfinz:balanced":            { inputPerMTok: 3,     outputPerMTok: 15,    cachedPerMTok: 0.30 },
  "askfinz:pro":                 { inputPerMTok: 15,    outputPerMTok: 75,    cachedPerMTok: 1.50 },
  "askfinz:fast":                { inputPerMTok: 0.80,  outputPerMTok: 4,     cachedPerMTok: 0.08 },
  "askfinz:bedrock-standard":    { inputPerMTok: 3,     outputPerMTok: 15,    cachedPerMTok: 0.30 },
  "askfinz:bedrock-fast":        { inputPerMTok: 0.80,  outputPerMTok: 4,     cachedPerMTok: 0.08 },
  "askfinz:bedrock-pro":         { inputPerMTok: 15,    outputPerMTok: 75,    cachedPerMTok: 1.50 },
  "gemini-2.5-flash":            { inputPerMTok: 0.15,  outputPerMTok: 0.60,  cachedPerMTok: 0.04,  freeTier: true },
  "gemini-3-flash-preview":      { inputPerMTok: 0.15,  outputPerMTok: 0.60,  cachedPerMTok: 0.04,  freeTier: true },
  "gemini-2.5-flash-lite":       { inputPerMTok: 0.10,  outputPerMTok: 0.40,  freeTier: true },
  "gemini-2.5-pro":              { inputPerMTok: 1.25,  outputPerMTok: 10,    cachedPerMTok: 0.31,  freeTier: true },
  "gemma-3-27b-it":              { inputPerMTok: 0,     outputPerMTok: 0,     free: true },
  "gemma-3-12b-it":              { inputPerMTok: 0,     outputPerMTok: 0,     free: true },
  "nvidia/deepseek-v4-pro":      { inputPerMTok: 0.27,  outputPerMTok: 1.10 },
  "nvidia/deepseek-v4-flash":    { inputPerMTok: 0.13,  outputPerMTok: 0.40 },
  "nvidia/llama-4-maverick":     { inputPerMTok: 0.20,  outputPerMTok: 0.60 },
  "nvidia/kimi-k2-instruct":     { inputPerMTok: 0.15,  outputPerMTok: 0.60 },
  "nvidia/minimax-m2.7":         { inputPerMTok: 0.20,  outputPerMTok: 0.70 },
  "nvidia/qwen3-coder-480b":     { inputPerMTok: 0.50,  outputPerMTok: 1.50 },
  "mistral-large-3":             { inputPerMTok: 2,     outputPerMTok: 6 },
  "mistral-medium-3":            { inputPerMTok: 0.40,  outputPerMTok: 2 },
  "magistral-small":             { inputPerMTok: 0.50,  outputPerMTok: 1.50 },
  "cerebras/qwen-3-235b":        { inputPerMTok: 0.60,  outputPerMTok: 0.60,  freeTier: true },
  "cerebras/llama-3.1-8b":       { inputPerMTok: 0.10,  outputPerMTok: 0.10,  freeTier: true },
  "groq/llama-4-scout":          { inputPerMTok: 0.11,  outputPerMTok: 0.34,  freeTier: true },
  "groq/llama-3.3-70b":          { inputPerMTok: 0.59,  outputPerMTok: 0.79,  freeTier: true },
  "groq/qwen3-32b":              { inputPerMTok: 0.29,  outputPerMTok: 0.59,  freeTier: true },
  "groq/compound":               { inputPerMTok: 0.11,  outputPerMTok: 0.34,  freeTier: true },
  "cohere/command-a":            { inputPerMTok: 2.50,  outputPerMTok: 10 },
  "cohere/command-r-plus":       { inputPerMTok: 2.50,  outputPerMTok: 10 },
  "github/gpt-5":                { inputPerMTok: 0,     outputPerMTok: 0,     freeTier: true },
  "github/gpt-4o":               { inputPerMTok: 0,     outputPerMTok: 0,     freeTier: true },
  "github/deepseek-r1":          { inputPerMTok: 0,     outputPerMTok: 0,     freeTier: true },
  "github/grok-3":               { inputPerMTok: 0,     outputPerMTok: 0,     freeTier: true },
  "github/phi-4":                { inputPerMTok: 0,     outputPerMTok: 0,     freeTier: true },
  "anthropic/claude-sonnet-4-6": { inputPerMTok: 3,     outputPerMTok: 15,    cachedPerMTok: 0.30 },
  "anthropic/claude-opus-4-7":   { inputPerMTok: 15,    outputPerMTok: 75,    cachedPerMTok: 1.50 },
  "deepseek/deepseek-r1":        { inputPerMTok: 0.55,  outputPerMTok: 2.19 },
  "x-ai/grok-3-beta":            { inputPerMTok: 3,     outputPerMTok: 15 },
}

// ─── UI-id → { provider, model } routing ─────────────────────────────────────
//
// These maps + resolveRoute() are the single source of truth for turning a
// picker model id (e.g. "askfinz:balanced", "groq/llama-3.3-70b") into the
// { provider, model } pair the chat backend expects. Extracted verbatim from
// chat-drawer.tsx runStream() so every surface routes identically.

export const _providerRouteMap: Record<string, string> = {
  google: "google_ai_studio", nvidia: "nvidia_nim",
  github: "github_models", askfinz: "openrouter", bedrock: "openrouter",
}

export const _askfinzModelMap: Record<string, string> = {
  "askfinz:balanced": "anthropic/claude-sonnet-4-6",
  "askfinz:pro": "anthropic/claude-opus-4-7",
  "askfinz:fast": "anthropic/claude-haiku-4-5",
  "askfinz:bedrock-standard": "anthropic/claude-sonnet-4-6",
  "askfinz:bedrock-fast": "anthropic/claude-haiku-4-5",
  "askfinz:bedrock-pro": "anthropic/claude-opus-4-7",
}

// Maps PROVIDER_MODELS ids to the actual API model IDs used by each provider
export const _modelIdOverride: Record<string, string> = {
  // Groq — actual Groq API model IDs
  "groq/llama-4-scout":  "meta-llama/llama-4-scout-17b-16e-instruct",
  "groq/llama-3.3-70b":  "llama-3.3-70b-versatile",
  "groq/qwen3-32b":      "qwen/qwen3-32b",
  "groq/compound":       "groq/compound",
  // NVIDIA NIM — third-party models use org-prefixed IDs
  "nvidia/deepseek-v4-pro":   "deepseek-ai/deepseek-v4-pro",
  "nvidia/deepseek-v4-flash": "deepseek-ai/deepseek-v4-flash",
  "nvidia/llama-4-maverick":  "meta/llama-4-maverick-17b-128e-instruct",
  "nvidia/kimi-k2-instruct":  "moonshotai/kimi-k2-instruct",
  "nvidia/minimax-m2.7":      "minimaxai/minimax-m2.7",
  "nvidia/qwen3-coder-480b":  "qwen/qwen3-coder-480b-a35b-instruct",
  // Cohere — versioned model IDs
  "cohere/command-a":       "command-a-03-2025",
  "cohere/command-r-plus":  "command-r-plus-08-2024",
  // Mistral — use stable latest aliases
  "mistral-large-3":   "mistral-large-latest",
  "mistral-medium-3":  "mistral-medium-latest",
  "magistral-small":   "magistral-small-latest",
  // Cerebras — map UI id to actual Cerebras model id
  "cerebras/qwen-3-235b": "qwen-3-235b-a22b-instruct-2507",
}

/**
 * Resolve a picker model id to the { provider, model } pair the chat backend
 * expects. Pure — reproduces chat-drawer.tsx runStream() mapping exactly:
 *   - unknown id or "auto"           → { provider:"auto",       model:"auto" }
 *   - askfinz | bedrock provider     → { provider:"openrouter", model via _askfinzModelMap }
 *   - openrouter provider            → passthrough (id is already the OpenRouter model id)
 *   - otherwise                      → _providerRouteMap for the provider, strip the UI
 *                                      prefix (groq/ | cerebras/ | cohere/ | github/),
 *                                      then _modelIdOverride for the final model id.
 */
export function resolveRoute(modelId: string): { provider: string; model: string } {
  const entry = PROVIDER_MODELS.find(m => m.id === modelId)
  if (!entry || modelId === "auto") {
    return { provider: "auto", model: "auto" }
  }
  if (entry.provider === "askfinz" || entry.provider === "bedrock") {
    return { provider: "openrouter", model: _askfinzModelMap[modelId] ?? "anthropic/claude-sonnet-4-6" }
  }
  if (entry.provider === "openrouter") {
    return { provider: "openrouter", model: modelId }
  }
  const provider = _providerRouteMap[entry.provider] ?? entry.provider
  const pfx = ["groq/", "cerebras/", "cohere/", "github/"].find(p => modelId.startsWith(p))
  let model = pfx ? modelId.slice(pfx.length) : modelId
  model = _modelIdOverride[modelId] ?? model
  return { provider, model }
}
